import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { KpiTile, PageHeader, Section } from "@/components/common/Layout";
import { DataTable, Pagination } from "@/components/common/DataTable";
import { StatusBadge, ScoreBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dt, duration, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const RATINGS = ["good", "needs_improvement", "bad", "wrong_outcome", "compliance_issue"];

const SCORING_CRITERIA = [
  "Correct introduction", "AI disclosure", "Product accuracy", "Relevance of questions",
  "Interruption handling", "Objection handling", "Naturalness", "Excessive repetition",
  "Unauthorized promises", "Respect for rejection", "Correct outcome classification",
  "Do-not-call compliance",
];

export default function Quality() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("pending");
  const [reviewing, setReviewing] = useState(null);
  const [form, setForm] = useState({ manual_rating: "good", notes: "", segments: "" });

  const { data, isLoading } = useQuery({
    queryKey: ["quality", page, status],
    queryFn: () => get("/quality/reviews", { page, page_size: 25, status: status || undefined }),
  });
  const { data: stats } = useQuery({ queryKey: ["quality-stats"], queryFn: () => get("/quality/stats") });

  const submit = useMutation({
    mutationFn: () =>
      api.post(`/quality/reviews/${reviewing.id}`, {
        manual_rating: form.manual_rating,
        notes: form.notes,
        training_segments: form.segments.split("\n").filter(Boolean),
      }),
    onSuccess: () => {
      toast.success("Review submitted");
      setReviewing(null);
      queryClient.invalidateQueries({ queryKey: ["quality"] });
      queryClient.invalidateQueries({ queryKey: ["quality-stats"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <>
      <PageHeader testId="quality-page" title="Quality control" subtitle="Automatic scoring plus human review of AI conversations" />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiTile testId="quality-kpi-pending" label="Pending reviews" value={stats?.pending ?? 0} tone="warning" />
        <KpiTile testId="quality-kpi-reviewed" label="Reviewed" value={stats?.reviewed ?? 0} tone="success" />
        <KpiTile testId="quality-kpi-score" label="Avg auto score" value={`${stats?.avg_quality_score ?? 0}/100`} />
        <KpiTile testId="quality-kpi-flags" label="Distinct flag types" value={(stats?.flags || []).length} tone="danger" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Section title="Automatic scoring criteria" testId="quality-criteria" className="lg:col-span-2">
          <ul className="grid grid-cols-1 gap-1.5 p-4 text-sm text-slate-700 md:grid-cols-2">
            {SCORING_CRITERIA.map((c) => (
              <li key={c} className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                {c}
              </li>
            ))}
          </ul>
        </Section>
        <Section title="Most frequent flags" testId="quality-flags">
          <ul className="divide-y divide-slate-100">
            {(stats?.flags || []).length === 0 ? (
              <li className="px-4 py-4 text-sm text-slate-500">No flags detected.</li>
            ) : (
              stats.flags.map((f) => (
                <li key={f._id} className="flex items-center justify-between px-4 py-2 text-sm">
                  <span className="text-slate-700">{title(f._id)}</span>
                  <span className="font-mono text-xs text-slate-500">{f.count}</span>
                </li>
              ))
            )}
          </ul>
        </Section>
      </div>

      <Section
        testId="quality-queue"
        title={`Review queue (${data?.total ?? 0})`}
        actions={
          <Select value={status || "all"} onValueChange={(v) => { setStatus(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger data-testid="quality-status-filter" className="h-9 w-40"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All reviews</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="reviewed">Reviewed</SelectItem>
            </SelectContent>
          </Select>
        }
      >
        <DataTable
          testId="quality-table"
          rowTestId="quality-row"
          loading={isLoading}
          columns={[
            { key: "contact", label: "Contact", render: (r) => <span className="font-medium text-slate-800">{r.call?.contact_name || "—"}</span> },
            { key: "campaign", label: "Campaign", render: (r) => r.call?.campaign_name || "—" },
            { key: "language", label: "Lang", render: (r) => (r.call?.language || "").toUpperCase() },
            { key: "outcome", label: "Outcome", render: (r) => (r.call?.outcome ? <StatusBadge value={r.call.outcome} /> : "—") },
            { key: "score", label: "Interest", render: (r) => <ScoreBadge score={r.call?.interest_score} /> },
            { key: "duration", label: "Duration", render: (r) => duration(r.call?.duration_seconds) },
            { key: "auto_score", label: "Auto score", render: (r) => `${r.auto_score ?? 0}/100` },
            { key: "auto_flags", label: "Flags", render: (r) => (r.auto_flags || []).map((f) => title(f)).join(", ") || "—" },
            { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
            { key: "manual_rating", label: "Rating", render: (r) => (r.manual_rating ? <StatusBadge value={r.manual_rating} /> : "—") },
            { key: "created_at", label: "Created", render: (r) => dt(r.created_at) },
            {
              key: "actions",
              label: "",
              render: (r) => (
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/calls/${r.call_id}`)} data-testid={`quality-open-call-${r.id}`}>
                    Call
                  </Button>
                  {can("quality", "write") ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-blue-600"
                      onClick={() => {
                        setReviewing(r);
                        setForm({ manual_rating: r.manual_rating || "good", notes: r.notes || "", segments: "" });
                      }}
                      data-testid={`quality-review-${r.id}`}
                    >
                      Review
                    </Button>
                  ) : null}
                </div>
              ),
            },
          ]}
          rows={data?.items || []}
          empty={{ title: "The review queue is empty", description: "Calls with compliance flags or low scores appear here automatically." }}
        />
        <Pagination page={data?.page || 1} pages={data?.pages || 1} total={data?.total || 0} onChange={setPage} />
      </Section>

      <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Review call · {reviewing?.call?.contact_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Automatic score {reviewing?.auto_score}/100 · flags:{" "}
              {(reviewing?.auto_flags || []).map((f) => title(f)).join(", ") || "none"}
            </p>
            <div className="space-y-1.5">
              <Label>Manual rating</Label>
              <Select value={form.manual_rating} onValueChange={(v) => setForm((f) => ({ ...f, manual_rating: v }))}>
                <SelectTrigger data-testid="quality-rating-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {RATINGS.map((r) => <SelectItem key={r} value={r}>{title(r)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Reviewer notes</Label>
              <Textarea data-testid="quality-notes" rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Approved training segments (one per line)</Label>
              <Textarea data-testid="quality-segments" rows={3} value={form.segments} onChange={(e) => setForm((f) => ({ ...f, segments: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => navigate(`/calls/${reviewing?.call_id}`)} data-testid="quality-view-call">
              Open call detail
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={submit.isPending} onClick={() => submit.mutate()} data-testid="quality-submit">
              Submit review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
